import { useMemo, useState } from "react";
import { Bell, ChevronLeft, ChevronRight, PawPrint, Plus, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppointmentCard } from "../components/AppointmentCard";
import { ReminderSheet } from "../components/ReminderSheet";
import { addDays, dateKeyFromIso, formatDayLabel, formatTime, isTodayOrFuture, toDateKey } from "../lib/date";
import { matchesAppointment } from "../lib/search";
import type { AppData, Appointment, AppointmentStatus, Id, Owner, Pet } from "../types";

interface AgendaPageProps {
  data: AppData;
  deleteAppointment(id: Id): void;
  setAppointmentStatus(id: Id, status: AppointmentStatus): void;
}

interface AppointmentView {
  appointment: Appointment;
  pet: Pet;
  owner: Owner;
}

export function AgendaPage({ data, deleteAppointment, setAppointmentStatus }: AgendaPageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = toDateKey(new Date());
  const requestedDate = searchParams.get("date");
  const selectedDate = requestedDate && /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : today;
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState<Id | null>(null);
  const [reminderId, setReminderId] = useState<Id | null>(null);

  const allAppointments = useMemo(() => data.appointments
    .map((appointment) => {
      const pet = data.pets.find((item) => item.id === appointment.petId);
      const owner = pet ? data.owners.find((item) => item.id === pet.ownerId) : undefined;
      return { appointment, pet, owner };
    })
    .filter((item): item is AppointmentView => Boolean(item.pet && item.owner)), [data]);
  const appointments = useMemo(() => allAppointments
    .filter(({ appointment }) => dateKeyFromIso(appointment.startsAt) === selectedDate)
    .sort((a, b) => a.appointment.startsAt.localeCompare(b.appointment.startsAt)), [allAppointments, selectedDate]);
  const searchResults = useMemo(() => query.trim()
    ? allAppointments
      .filter(({ appointment, pet, owner }) => matchesAppointment(query, appointment, pet, owner))
      .sort((a, b) => a.appointment.startsAt.localeCompare(b.appointment.startsAt))
      .slice(0, 8)
    : [], [allAppointments, query]);
  const pendingCount = data.appointments.filter((item) => item.status === "pending" && isTodayOrFuture(item.startsAt)).length;
  const reminderAppointment = data.appointments.find((item) => item.id === reminderId) || null;
  const reminderPet = reminderAppointment ? data.pets.find((item) => item.id === reminderAppointment.petId) || null : null;
  const reminderOwner = reminderPet ? data.owners.find((item) => item.id === reminderPet.ownerId) || null : null;

  function changeDay(amount: number) {
    setSearchParams({ date: addDays(selectedDate, amount) });
    setExpandedId(null);
  }

  function chooseSearchResult(result: AppointmentView) {
    setSearchParams({ date: dateKeyFromIso(result.appointment.startsAt) });
    setExpandedId(result.appointment.id);
    setQuery("");
  }

  return (
    <main className="app-shell">
      <header className="top-bar">
        <div className="brand"><span className="brand__mark"><PawPrint size={22} /></span><span>Psi Fryzjer <small>w chmurze</small></span></div>
        <div className="top-bar__actions">
          <button className="pending-button" type="button" aria-label={`Niepotwierdzone wizyty: ${pendingCount}`} onClick={() => navigate("/pending")}><Bell size={18} /><span>{pendingCount}</span></button>
          <button className="add-button" type="button" aria-label="Dodaj wizytę" title="Dodaj wizytę" onClick={() => navigate(`/appointments/new?date=${selectedDate}`)}><Plus size={23} /></button>
        </div>
      </header>

      <section className="agenda-intro"><h1>Wizyty</h1></section>

      <nav className="day-navigation" aria-label="Wybór dnia">
        <button className="icon-button" type="button" onClick={() => changeDay(-1)} aria-label="Poprzedni dzień"><ChevronLeft size={22} /></button>
        <button className="day-navigation__label" type="button" onClick={() => setSearchParams({ date: today })}>{formatDayLabel(selectedDate)}</button>
        <button className="icon-button" type="button" onClick={() => changeDay(1)} aria-label="Następny dzień"><ChevronRight size={22} /></button>
      </nav>

      <div className="global-search">
        <label className="search-field">
          <Search size={19} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Szukaj psa, właściciela, telefonu" aria-label="Szukaj wizyty" />
        </label>
        {query.trim() && (
          <div className="global-search-results">
            {searchResults.length ? searchResults.map((result) => (
              <button key={result.appointment.id} type="button" onClick={() => chooseSearchResult(result)}>
                <span className="global-search-results__avatar">{result.pet.avatar || "🐾"}</span>
                <span><strong>{result.pet.name}</strong><small>{result.owner.fullName}</small></span>
                <span className="global-search-results__date"><strong>{formatDayLabel(dateKeyFromIso(result.appointment.startsAt))}</strong><small>{formatTime(result.appointment.startsAt)}</small></span>
              </button>
            )) : <p>Brak wyników wyszukiwania.</p>}
          </div>
        )}
      </div>

      <section className="appointment-list" aria-live="polite">
        {appointments.length ? appointments.map(({ appointment, pet, owner }) => (
          <AppointmentCard
            key={appointment.id}
            appointment={appointment}
            pet={pet}
            owner={owner}
            expanded={expandedId === appointment.id}
            onToggle={() => setExpandedId((current) => (current === appointment.id ? null : appointment.id))}
            onRemind={() => setReminderId(appointment.id)}
            onToggleStatus={() => setAppointmentStatus(appointment.id, appointment.status === "pending" ? "confirmed" : "pending")}
            onEdit={() => navigate(`/appointments/${appointment.id}/edit`)}
            onDelete={() => { deleteAppointment(appointment.id); setExpandedId(null); }}
          />
        )) : <div className="empty-state"><span>🐾</span><h2>Brak wizyt</h2><p>Nie ma jeszcze wizyt na ten dzień.</p></div>}
      </section>

      <ReminderSheet open={Boolean(reminderId)} appointment={reminderAppointment} pet={reminderPet} owner={reminderOwner} onClose={() => setReminderId(null)} />
    </main>
  );
}
